import { useCallback } from 'react';
import type { PermissionKey } from '@safetyhub/shared';
import { useAuthStore } from '../stores/auth';
import { hasSiteAssignment } from '../utils/siteAccess';

/**
 * 조직 권한 훅.
 *
 * `can(key)`는 조직 RBAC(줄기 company-rbac, 결정 `2026-09-20-company-rbac-with-legal-positions` §5)의
 * 판정 정본 — `/auth/me`가 실은 `currentMembership.permissions`(보유 역할 합집합, owner는 전체 키)에
 * 그 권한 key가 있는가만 본다. 역할 key 비교는 하지 않는다(조직이 만든 역할이 코드 수정 없이 동작해야
 * 하므로). 멤버십이 없거나 `permissions`가 비어 있으면 false.
 *
 * P4(판정 이관 ①)부터 옛 `role`·`canManage*` 폴백은 없다:
 * - `isOwner` = `currentMembership.isOwner`(서버 `resolvePermissions().isOwner` — 소유자 **성질**. 마지막 소유자
 *   보호·조직 삭제·소유권 이전 화면처럼 권한 key로 못 푸는 판정에만 쓴다. 「이 행위를 해도 되나」는 `can(key)`)
 * - `canManageSites` = `can('site.manage')`, `canManageInspections` = `can('inspection.manage')` — 이름은 소비처
 *   호환용으로 남긴 별칭이다. 옛 현장 3상태 오버라이드(`SiteAssignment.canManageInspections`)는 P7-a에서 컬럼째
 *   drop됐다 — `siteId`는 이제 `isSiteAssigned`(배정 여부)에만 쓰이고, 키 판정은 현장과 무관하게 역할 합집합 하나다
 *   (서버 `hasPermission(companyId, memberId, key)`와 같은 규칙).
 */
export function usePermissions(siteId?: string) {
  const { currentMembership } = useAuthStore();

  const permissions = currentMembership?.permissions;
  /**
   * ⚠️ `key`는 **유니온**이다(shared `PermissionKey`) — 생 문자열을 받으면 오타(`inspecton.create`)가
   * 타입에 걸리지 않고 **영영 false**로 평가된다(화면은 그 기능이 없는 것처럼 보일 뿐이라 조용하다).
   * 목록은 서버 `PERMISSION_DEFINITIONS`의 **사본**이고 집합 동일은 서버 spec이 잰다(shared 그 파일 doc 참조).
   */
  const can = useCallback(
    (key: PermissionKey): boolean => Array.isArray(permissions) && permissions.includes(key),
    [permissions],
  );

  const isOwner = currentMembership?.isOwner === true;

  // ⚠️ 이 `isSiteAssigned`는 **배정 행이 있나**만 묻는다 — owner 특례가 없다(소비처가
  // `isOrgOwner || (isSiteAssigned && …)`로 소유자 항을 밖에서 얹는다). 「이 현장에 닿을 수
  // 있나」(owner ⇒ 전 현장)는 같은 이름의 다른 함수인 `utils/siteAccess.isSiteAssigned`다.
  // 둘의 차이와 「합치지 말 것」의 근거는 그 파일 doc에 있다. 조회 조각만 공유한다.
  const isSiteAssigned = hasSiteAssignment(currentMembership, siteId);

  return {
    isOwner,
    isSiteAssigned,
    canManageSites: can('site.manage'),
    canManageInspections: can('inspection.manage'),
    can,
  };
}
